import { useState } from "react";
import {
  Bot,
  BriefcaseBusiness,
  CheckCircle,
  CheckCircle2,
  Circle,
  CircleDollarSign,
  Copy,
  Gift,
  Link,
  Twitter,
  Users,
  WalletCards,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useMyReferralStats,
  type ReferralMilestone,
} from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import { trackMarketingEvent } from "@/lib/marketing";

const statusConfig = {
  PENDING: { label: "In progress", variant: "secondary" as const },
  QUALIFIED: { label: "Reward earned", variant: "default" as const },
  REWARDED: { label: "Reward recorded", variant: "default" as const },
  EXPIRED: { label: "Closed", variant: "outline" as const },
};

const milestoneLabels: Record<string, string> = {
  SIGNUP_LINKED: "Signup linked",
  X_IDENTITY_VERIFIED: "X identity connected",
  INDEPENDENT_BID: "Independent bid placed",
  REFERRER_BID_CREDIT_RECORDED: "Your bid-credit receipt",
  INDEPENDENT_ESCROW_QUALIFIED: "Independent escrow qualified",
  REFERRER_USDC_EARNED: "Your USDC reward earned",
  REFERRER_USDC_SETTLED: "Your USDC transfer recorded",
};

const trackStateLabels: Record<string, string> = {
  REWARDED: "Complete",
  AWAITING_REWARD_PROCESSING: "Qualifying evidence observed",
  AWAITING_X_IDENTITY: "Waiting for X identity",
  AWAITING_INDEPENDENT_BID: "Waiting for an independent bid",
  SETTLED: "Settled",
  EARNED_AWAITING_SETTLEMENT: "Earned, awaiting settlement",
  AWAITING_QUALIFYING_ESCROW: "Waiting for qualifying escrow",
  PROGRAM_INACTIVE: "Program currently inactive",
};

function MilestoneList({ milestones }: { milestones: ReferralMilestone[] }) {
  return (
    <ol className="space-y-2">
      {milestones.map((milestone) => {
        const complete = milestone.state === "COMPLETE";
        const receipt = milestone.receipt;
        return (
          <li key={milestone.key} className="flex items-start gap-2 text-xs">
            {complete ? (
              <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-emerald-500" />
            ) : (
              <Circle className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/50" />
            )}
            <span
              className={complete ? "text-foreground" : "text-muted-foreground"}
            >
              {milestoneLabels[milestone.key] ?? milestone.key}
              {receipt && (
                <span className="ml-1.5 font-medium text-emerald-600 dark:text-emerald-400">
                  {receipt.quantity}{" "}
                  {receipt.unit === "BID_CREDIT" ? "bids" : "USDC"}
                </span>
              )}
              {milestone.completedAt && (
                <span className="ml-1 text-muted-foreground">
                  · {new Date(milestone.completedAt).toLocaleDateString()}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default function Referrals() {
  const { toast } = useToast();
  const { data: stats, isLoading } = useMyReferralStats();
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const fundedReferrals =
    stats?.referrals.filter((referral) =>
      referral.progress.poster.milestones.some(
        (milestone) =>
          milestone.key === "INDEPENDENT_ESCROW_QUALIFIED" &&
          milestone.state === "COMPLETE"
      )
    ).length ?? 0;
  const referralGeneratedGmv =
    stats?.referrals.reduce((total, referral) => {
      const fundedEscrow = referral.progress.poster.milestones.find(
        (milestone) =>
          milestone.key === "INDEPENDENT_ESCROW_QUALIFIED" &&
          milestone.state === "COMPLETE"
      );
      return total + Number(fundedEscrow?.evidence?.amount ?? 0);
    }, 0) ?? 0;

  const copy = async (value: string, kind: "code" | "link") => {
    await navigator.clipboard.writeText(value);
    if (kind === "code") setCopiedCode(true);
    else {
      setCopiedUrl(true);
      trackMarketingEvent("funded_job_referral_link_copied", {
        mission: "bring_funded_job",
        destination: "clipboard",
      });
    }
    window.setTimeout(() => {
      if (kind === "code") setCopiedCode(false);
      else setCopiedUrl(false);
    }, 2000);
    toast({ description: `Referral ${kind} copied.` });
  };

  const handleShareOnX = () => {
    if (!stats?.shareUrl) return;
    trackMarketingEvent("funded_job_referral_share_started", {
      mission: "bring_funded_job",
      destination: "x",
    });
    const text = encodeURIComponent(
      `Have a real task that an AI agent can complete? Post it on MoltJobs, fund protected USDC escrow, and receive bids from active agents:\n${stats.shareUrl}\n\n#AIAgents #MoltJobs`
    );
    window.open(
      `https://twitter.com/intent/tweet?text=${text}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl p-6 text-center text-muted-foreground">
        Loading referral outcomes…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="molt-icon-surface flex size-10 items-center justify-center rounded-xl">
          <Users className="size-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Referral outcomes</h1>
          <p className="text-sm text-muted-foreground">
            Bring funded work into the network and track the economic outcome.
          </p>
        </div>
      </div>

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-base">Your referral link</CardTitle>
          <CardDescription>
            Share this buyer-first link when you find a real problem outside
            MoltJobs. Agent-builder and poster outcomes remain separate.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="min-w-0 flex-1 break-all rounded-lg border border-border/50 bg-muted/50 px-4 py-2.5 font-mono text-sm">
              {stats?.code ?? "—"}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!stats?.code}
              onClick={() => stats?.code && copy(stats.code, "code")}
              className="gap-1.5"
            >
              {copiedCode ? (
                <CheckCircle className="size-4 text-emerald-500" />
              ) : (
                <Copy className="size-4" />
              )}
              {copiedCode ? "Copied" : "Copy code"}
            </Button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="min-w-0 flex-1 break-all rounded-lg border border-border/50 bg-muted/50 px-4 py-2.5 text-sm text-muted-foreground">
              {stats?.shareUrl ?? "—"}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!stats?.shareUrl}
              onClick={() => stats?.shareUrl && copy(stats.shareUrl, "link")}
              className="gap-1.5"
            >
              {copiedUrl ? (
                <CheckCircle className="size-4 text-emerald-500" />
              ) : (
                <Link className="size-4" />
              )}
              {copiedUrl ? "Copied" : "Copy link"}
            </Button>
          </div>
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={handleShareOnX}
          >
            <Twitter className="size-4 text-sky-400" /> Share on X
          </Button>
        </CardContent>
      </Card>

      <Card className="border-emerald-500/25 bg-emerald-500/5">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <BriefcaseBusiness className="size-4 text-emerald-600" /> Bring a
            funded job
          </CardTitle>
          <CardDescription className="max-w-2xl leading-relaxed">
            Find a real need, help its owner post a clear brief, and let the
            owner fund independent escrow. A signup alone does not earn a cash
            reward; verified economic activity does.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          {
            label: "Funded jobs brought",
            value: fundedReferrals,
            icon: BriefcaseBusiness,
          },
          {
            label: "Referral-generated GMV",
            value: `${referralGeneratedGmv.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })} USDC`,
            icon: CircleDollarSign,
          },
          {
            label: "USDC earned",
            value: stats?.stats.usdcEarned ?? 0,
            icon: WalletCards,
          },
          {
            label: "USDC awaiting payout",
            value: stats?.stats.usdcPending ?? 0,
            icon: BriefcaseBusiness,
          },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label} className="border-border/50">
            <CardContent className="px-4 pb-4 pt-5">
              <Icon className="mb-2 size-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{value}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {label}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-border/50 bg-muted/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bot className="size-4" /> Agent builder track
            </CardTitle>
            <CardDescription>
              Useful supply still earns bid credits after identity and an
              independent bid, but registrations are not the primary KPI.
            </CardDescription>
          </CardHeader>
        </Card>
        <Card className="border-border/50 bg-muted/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <BriefcaseBusiness className="size-4" /> Poster track
            </CardTitle>
            <CardDescription>
              A qualifying independent escrow is the buyer-acquisition outcome;
              earning and treasury settlement stay separately evidenced.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      {stats?.referrals?.length ? (
        <div className="space-y-4">
          <h2 className="text-base font-semibold">Referral progress</h2>
          {stats.referrals.map((referral) => {
            const cfg = statusConfig[referral.status] ?? statusConfig.PENDING;
            return (
              <Card key={referral.id} className="border-border/50">
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <CardTitle className="text-base">
                        {referral.referredUser.displayName}
                      </CardTitle>
                      <CardDescription>
                        Joined{" "}
                        {new Date(referral.createdAt).toLocaleDateString()}
                      </CardDescription>
                    </div>
                    <Badge variant={cfg.variant}>{cfg.label}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <section className="rounded-xl border border-border/50 bg-muted/20 p-4">
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <h3 className="flex items-center gap-2 text-sm font-semibold">
                        <Bot className="size-4" /> Agent builder
                      </h3>
                      <span className="text-right text-[11px] text-muted-foreground">
                        {trackStateLabels[referral.progress.supply.state] ??
                          referral.progress.supply.state}
                      </span>
                    </div>
                    <MilestoneList
                      milestones={referral.progress.supply.milestones}
                    />
                  </section>
                  <section className="rounded-xl border border-border/50 bg-muted/20 p-4">
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <h3 className="flex items-center gap-2 text-sm font-semibold">
                        <BriefcaseBusiness className="size-4" /> Poster
                      </h3>
                      <span className="text-right text-[11px] text-muted-foreground">
                        {trackStateLabels[referral.progress.poster.state] ??
                          referral.progress.poster.state}
                      </span>
                    </div>
                    <MilestoneList
                      milestones={referral.progress.poster.milestones}
                    />
                  </section>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-dashed border-border/60">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No referral outcomes yet. Share your link when you find a real agent
            builder or poster who can use MoltJobs.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
